import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
} from "react";
import {
  PaletteIcon,
  BlocksIcon,
  ArchiveIcon,
  ArrowLeftIcon,
  BotIcon,
  FolderIcon,
  GitBranchIcon,
  GlobeIcon,
  KeyboardIcon,
  MessageSquareTextIcon,
  NetworkIcon,
  PlugIcon,
  RefreshCwIcon,
  SearchIcon,
  Settings2Icon,
  ShieldIcon,
  TreesIcon,
  UserRoundIcon,
  UsersRoundIcon,
  XIcon,
  createLucideIcon,
} from "lucide-react";
import { useCanGoBack, useLocation, useNavigate } from "@tanstack/react-router";

import { hasServerExposureBridge } from "../../connection/developmentOnlyRoutes";
import { useConnectAdmin } from "../../hooks/useConnectAdmin";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Kbd } from "../ui/kbd";
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "../ui/sidebar";
import { useSettingsScopeManagedByAfar } from "../../hooks/useEnvironmentManagedByAfar";
import { readSettingsClientBridges } from "./settingsClientBridges";
import {
  resolveSettingsNavigationGroups,
  resolveSettingsNavigationPaths,
  type SettingsSectionPath,
} from "./settingsNavigation.logic";
import { scrollToSettingsTarget } from "./settingsLayout";
import {
  searchSettings,
  settingsSearchItems,
  SETTINGS_SECTION_LABELS,
  type SettingsPath,
  type SettingsSearchItem,
} from "./settingsSearch";

const SnapShotIcon = createLucideIcon("snap-shot", [
  [
    "path",
    {
      d: "M8 3H6a3 3 0 0 0-3 3v2M16 3h2a3 3 0 0 1 3 3v2M21 16v2a3 3 0 0 1-3 3h-2M8 21H6a3 3 0 0 1-3-3v-2",
      key: "capture-frame",
    },
  ],
  ["rect", { width: "10", height: "8", x: "7", y: "8", rx: "2", key: "window" }],
  ["circle", { cx: "12", cy: "12", r: "1.5", key: "lens" }],
]);

const SETTINGS_SECTION_ICONS: Readonly<
  Record<SettingsSectionPath, ComponentType<{ className?: string }>>
> = {
  "/settings/general": Settings2Icon,
  "/settings/projects": FolderIcon,
  "/settings/appearance": PaletteIcon,
  "/settings/snap-shot": SnapShotIcon,
  "/settings/integrations": PlugIcon,
  "/settings/keybindings": KeyboardIcon,
  "/settings/providers": BotIcon,
  "/settings/mcp-servers": BlocksIcon,
  "/settings/source-control": GitBranchIcon,
  "/settings/worktrees": TreesIcon,
  "/settings/admin": ShieldIcon,
  "/settings/account": UserRoundIcon,
  "/settings/connections": NetworkIcon,
  "/settings/network": GlobeIcon,
  "/settings/organizations": UsersRoundIcon,
  "/settings/updates": RefreshCwIcon,
  "/settings/feedback": MessageSquareTextIcon,
  "/settings/archived": ArchiveIcon,
};

function settingsNavItems(paths: ReadonlyArray<SettingsSectionPath>) {
  return paths.map((to) => ({
    icon: SETTINGS_SECTION_ICONS[to],
    label: SETTINGS_SECTION_LABELS[to],
    to,
  }));
}

function SettingsSectionIcon({ to }: { readonly to: SettingsPath }) {
  const Icon = to === "/settings/diagnostics" ? Settings2Icon : SETTINGS_SECTION_ICONS[to];
  return <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/60" />;
}

export function SettingsSidebarNav({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const currentHash = useLocation({ select: (location) => location.hash });
  const currentEnvironmentPage = useLocation({
    select: (location) => (location.search as { environment?: string } | undefined)?.environment,
  });
  const { isMobile, setOpenMobile, open, setOpen } = useSidebar();
  const admin = useConnectAdmin();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const managedByAfar = useSettingsScopeManagedByAfar();
  const navInput = useMemo(
    () => ({ ...readSettingsClientBridges(), admin, managedByAfar }),
    [admin, managedByAfar],
  );
  const navGroups = useMemo(
    () =>
      resolveSettingsNavigationGroups(navInput).map((group) => ({
        label: group.label,
        items: settingsNavItems(group.paths),
      })),
    [navInput],
  );
  // In sidebar order, so search lists results the way the sidebar lists sections.
  const availableSearchPaths = useMemo(
    () =>
      new Set<SettingsPath>([...resolveSettingsNavigationPaths(navInput), "/settings/diagnostics"]),
    [navInput],
  );
  const searchItems = useMemo(
    () => settingsSearchItems({ serverExposureAvailable: hasServerExposureBridge() }),
    [],
  );
  const results = useMemo(
    () => searchSettings(query, searchItems, availableSearchPaths),
    [availableSearchPaths, query, searchItems],
  );
  const isSearching = query.trim().length > 0;
  const hasResults = results.length > 0;

  useEffect(() => {
    const result = results[activeResultIndex];
    if (!result) return;
    document
      .getElementById(`settings-search-result-${result.id}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeResultIndex, results]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.closest('[role="dialog"], [aria-modal="true"], [data-slot$="popup"]') !== null)
      ) {
        return;
      }

      event.preventDefault();
      if (isMobile) {
        setOpenMobile(true);
      } else if (!open) {
        void setOpen(true);
      }
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      });
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, open, setOpen, setOpenMobile]);

  const handleSectionClick = useCallback(
    (to: SettingsSectionPath) => {
      if (isMobile) setOpenMobile(false);
      void navigate({ to, hash: "", replace: true, hashScrollIntoView: false });
    },
    [isMobile, navigate, setOpenMobile],
  );
  const clearSearch = useCallback(() => {
    setQuery("");
    setActiveResultIndex(0);
  }, []);
  const handleSearchResultClick = useCallback(
    (item: SettingsSearchItem) => {
      clearSearch();
      if (isMobile) setOpenMobile(false);
      const targetId = item.targetId ?? item.id;
      if (
        pathname === item.to &&
        currentHash.replace(/^#/, "") === targetId &&
        currentEnvironmentPage === item.search?.environment
      ) {
        scrollToSettingsTarget(targetId);
        return;
      }
      void navigate({
        to: item.to,
        // A row that lives on an Environment's page opens that page; any other leaves one.
        search: (previous) => {
          const { environment: _environment, ...rest } = previous as Record<string, unknown>;
          return item.search ? { ...rest, ...item.search } : rest;
        },
        hash: targetId,
        replace: true,
        hashScrollIntoView: false,
      });
    },
    [clearSearch, currentEnvironmentPage, currentHash, isMobile, navigate, pathname, setOpenMobile],
  );
  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape" && isSearching) {
        event.preventDefault();
        event.stopPropagation();
        clearSearch();
        return;
      }
      if (results.length === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveResultIndex((index) => (index + 1) % results.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveResultIndex((index) => (index - 1 + results.length) % results.length);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const result = results[activeResultIndex];
        if (result) handleSearchResultClick(result);
      }
    },
    [activeResultIndex, clearSearch, handleSearchResultClick, isSearching, results],
  );
  const handleBackClick = useCallback(() => {
    if (isMobile) setOpenMobile(false);
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: "/" });
  }, [canGoBack, isMobile, navigate, setOpenMobile]);

  return (
    <>
      <SidebarContent className="overflow-x-hidden">
        <SidebarGroup className="gap-2 px-2 py-3">
          <div className="flex h-8 items-center gap-2 rounded-lg px-2 text-sm text-muted-foreground focus-within:bg-accent focus-within:text-foreground">
            <SearchIcon className="size-4 shrink-0" />
            <Input
              ref={searchInputRef}
              nativeInput
              unstyled
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.currentTarget.value);
                setActiveResultIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search"
              aria-label="Search settings"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={isSearching && hasResults}
              aria-controls={isSearching && hasResults ? "settings-search-results" : undefined}
              aria-activedescendant={
                isSearching && results[activeResultIndex]
                  ? `settings-search-result-${results[activeResultIndex].id}`
                  : undefined
              }
              className="min-w-0 flex-1 [&_[data-slot=input]]:h-auto [&_[data-slot=input]]:p-0 [&_[data-slot=input]]:text-sm [&_[data-slot=input]]:leading-normal"
            />
            {isSearching ? (
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                className="size-6 shrink-0"
                aria-label="Clear settings search"
                onClick={() => {
                  clearSearch();
                  searchInputRef.current?.focus();
                }}
              >
                <XIcon className="size-3" />
              </Button>
            ) : (
              <Kbd className="h-4 min-w-0 rounded-sm px-1.5 text-[10px]">/</Kbd>
            )}
          </div>

          {isSearching && !hasResults ? (
            <p role="status" className="px-2 py-6 text-center text-xs text-muted-foreground">
              No settings found
            </p>
          ) : null}

          {isSearching ? (
            <SidebarMenu
              id={hasResults ? "settings-search-results" : undefined}
              role={hasResults ? "listbox" : undefined}
              aria-label={hasResults ? "Settings search results" : undefined}
            >
              {results.map((item, index) => (
                <SidebarMenuItem key={item.id} role="presentation">
                  <SidebarMenuButton
                    id={`settings-search-result-${item.id}`}
                    role="option"
                    aria-selected={index === activeResultIndex}
                    tabIndex={-1}
                    size="sm"
                    isActive={index === activeResultIndex}
                    className="h-auto min-h-10 items-start gap-2 px-2 py-2"
                    onMouseMove={() => setActiveResultIndex(index)}
                    onClick={() => handleSearchResultClick(item)}
                  >
                    <SettingsSectionIcon to={item.to} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{item.title}</span>
                      <span className="block truncate text-[11px] text-muted-foreground/75">
                        {SETTINGS_SECTION_LABELS[item.to]}
                      </span>
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          ) : (
            navGroups.map((group) => (
              <div key={group.label} role="group" aria-label={group.label}>
                <SidebarGroupLabel className="h-6 px-2.5 text-[11px] font-medium text-muted-foreground/60">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          size="sm"
                          isActive={isActive}
                          className={
                            isActive
                              ? "gap-2.5 px-2.5 py-2 text-left text-[13px] font-medium text-foreground"
                              : "gap-2.5 px-2.5 py-2 text-left text-[13px] text-muted-foreground/70 hover:text-foreground/80"
                          }
                          onClick={() => handleSectionClick(item.to)}
                        >
                          <Icon className="size-4 shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </div>
            ))
          )}
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator />
      <SidebarFooter className="p-2">
        <SidebarMenu className="min-w-0">
          <SidebarMenuItem>
            <SidebarMenuButton
              size="sm"
              className="gap-2 px-2 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={handleBackClick}
            >
              <ArrowLeftIcon className="size-4" />
              <span>Back</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </>
  );
}
